import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { User, Envelope, Phone, Lock, PencilSimple, Check, Share, Copy, Gift, WhatsappLogo, Cake } from "@phosphor-icons/react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Profile = () => {
  const { user, getAuthHeaders, checkAuth } = useAuth();
  const [editing, setEditing] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({ name: user?.name || "", phone: user?.phone || "", birth_date: user?.birth_date || "" });
  const [passwordData, setPasswordData] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [referral, setReferral] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/referral/me`, { headers: getAuthHeaders(), withCredentials: true })
      .then(r => setReferral(r.data)).catch(() => {});
  }, []);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/auth/profile`, profileData, {
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      toast.success("Perfil atualizado!");
      setEditing(false);
      await checkAuth();
    } catch (error) {
      toast.error("Erro ao atualizar perfil");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("As senhas não coincidem");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      toast.success("Senha alterada com sucesso!");
      setChangingPassword(false);
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao alterar senha");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <h2 className="text-2xl sm:text-3xl font-bold text-[#042F2E] mb-2">Meu Perfil</h2>
      <p className="text-[#115E59] mb-8">Gerencie seus dados pessoais</p>

      {/* Profile Info */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl p-8 border border-[#E4DFD3] shadow-sm mb-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-[#042F2E]">Dados Pessoais</h3>
          {!editing && (
            <Button
              onClick={() => setEditing(true)}
              variant="ghost"
              className="text-[#FF5A26] font-bold"
              data-testid="edit-profile-btn"
            >
              <PencilSimple size={20} className="mr-2" /> Editar
            </Button>
          )}
        </div>

        {!editing ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-[#F4F1EA] rounded-xl">
              <User size={24} weight="duotone" className="text-[#115E59]" />
              <div>
                <p className="text-xs font-bold text-[#115E59] uppercase tracking-wider">Nome</p>
                <p className="text-[#042F2E] font-semibold" data-testid="profile-name">{user?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-[#F4F1EA] rounded-xl">
              <Envelope size={24} weight="duotone" className="text-[#115E59]" />
              <div>
                <p className="text-xs font-bold text-[#115E59] uppercase tracking-wider">Email</p>
                <p className="text-[#042F2E] font-semibold" data-testid="profile-email">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-[#F4F1EA] rounded-xl">
              <Phone size={24} weight="duotone" className="text-[#115E59]" />
              <div>
                <p className="text-xs font-bold text-[#115E59] uppercase tracking-wider">Telefone</p>
                <p className="text-[#042F2E] font-semibold" data-testid="profile-phone">{user?.phone || "Não informado"}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-[#F4F1EA] rounded-xl">
              <Cake size={24} weight="duotone" className="text-[#115E59]" />
              <div>
                <p className="text-xs font-bold text-[#115E59] uppercase tracking-wider">Data de Nascimento</p>
                <p className="text-[#042F2E] font-semibold" data-testid="profile-birthdate">
                  {user?.birth_date ? new Date(user.birth_date + "T12:00:00").toLocaleDateString("pt-BR") : "Não informado"}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div>
              <Label className="text-sm font-bold text-[#115E59]">Nome</Label>
              <Input
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                className="rounded-xl border-[#E4DFD3]"
                data-testid="edit-name-input"
              />
            </div>
            <div>
              <Label className="text-sm font-bold text-[#115E59]">Telefone</Label>
              <Input
                value={profileData.phone}
                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                className="rounded-xl border-[#E4DFD3]"
                data-testid="edit-phone-input"
              />
            </div>
            <div>
              <Label className="text-sm font-bold text-[#115E59]">Data de Nascimento</Label>
              <Input
                type="date"
                value={profileData.birth_date}
                onChange={(e) => setProfileData({ ...profileData, birth_date: e.target.value })}
                className="rounded-xl border-[#E4DFD3]"
                data-testid="edit-birthdate-input"
              />
            </div>
            <div className="flex gap-3">
              <Button type="submit" disabled={loading} className="bg-[#FF5A26] hover:bg-[#E04818] text-white rounded-full" data-testid="save-profile-btn">
                <Check size={20} className="mr-2" /> {loading ? "Salvando..." : "Salvar"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setEditing(false)} className="rounded-full">Cancelar</Button>
            </div>
          </form>
        )}
      </motion.div>

      {/* Change Password */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl p-8 border border-[#E4DFD3] shadow-sm"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-[#042F2E]">Segurança</h3>
        </div>

        {!changingPassword ? (
          <Button
            onClick={() => setChangingPassword(true)}
            variant="outline"
            className="border-[#FF5A26] text-[#FF5A26] hover:bg-[#FF5A26] hover:text-white rounded-full"
            data-testid="change-password-btn"
          >
            <Lock size={20} className="mr-2" /> Alterar Senha
          </Button>
        ) : (
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label className="text-sm font-bold text-[#115E59]">Senha Atual</Label>
              <Input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                className="rounded-xl border-[#E4DFD3]"
                required
                data-testid="current-password-input"
              />
            </div>
            <div>
              <Label className="text-sm font-bold text-[#115E59]">Nova Senha</Label>
              <Input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                className="rounded-xl border-[#E4DFD3]"
                required
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label className="text-sm font-bold text-[#115E59]">Confirmar Nova Senha</Label>
              <Input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                className="rounded-xl border-[#E4DFD3]"
                required
                data-testid="confirm-password-input"
              />
            </div>
            <div className="flex gap-3">
              <Button type="submit" disabled={loading} className="bg-[#FF5A26] hover:bg-[#E04818] text-white rounded-full" data-testid="save-password-btn">
                {loading ? "Alterando..." : "Alterar Senha"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setChangingPassword(false)} className="rounded-full">Cancelar</Button>
            </div>
          </form>
        )}
      </motion.div>

      {/* Referral / Indicação */}
      {referral && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-[#FF5A26] to-[#E04818] rounded-2xl p-8 mt-6 text-white"
          data-testid="referral-section"
        >
          <div className="flex items-center gap-3 mb-4">
            <Gift size={28} weight="fill" />
            <h3 className="text-xl font-bold">Indique e Ganhe!</h3>
          </div>
          <p className="text-white/90 mb-4 text-sm">
            Convide amigos para o MARABU e ganhe <strong>100 pontos</strong> por cada indicação!
          </p>
          <div className="bg-white/20 rounded-xl p-3 flex items-center gap-2 mb-4">
            <span className="font-mono font-bold text-lg flex-1 truncate">{referral.referral_code}</span>
            <Button onClick={() => { navigator.clipboard.writeText(referral.referral_link); toast.success("Link copiado!"); }}
              className="bg-white text-[#FF5A26] hover:bg-white/90 rounded-full shrink-0" data-testid="copy-referral">
              <Copy size={18} weight="bold" className="mr-1" /> Copiar
            </Button>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => {
              const msg = `Venha para o MARABU OPEN CLUB! Use meu código ${referral.referral_code} no cadastro.\n${referral.referral_link}`;
              window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
            }} className="bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-full flex-1" data-testid="share-referral-wa">
              <WhatsappLogo size={20} weight="fill" className="mr-2" /> Compartilhar
            </Button>
          </div>
          {referral.referral_count > 0 && (
            <p className="text-white/80 text-sm mt-4">Você já indicou <strong>{referral.referral_count}</strong> pessoa(s)!</p>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default Profile;
